import type { ConnectorDTO } from "../../../shared/types";
import { usedByLine } from "../../lib/connectors";
import { Table } from "../../components/Table";
import { Tabs, useTabParam } from "../../components/Tabs";
import { TextLink } from "../../components/TextLink";
import { Badge, timeAgo } from "../../components/ui";
import { levelLabel, type ConnectorAccess } from "./ConnectorWays";

export const ACCESS_FILTERS = ["all", "agents", "people"] as const;

type Row = {
  key: string;
  kind: "agent" | "person" | "service";
  name: string;
  to: string;
  detail: string | null;
  level: string;
  login: "connected" | "lapsed" | null;
  lastUsedAt: string | null;
};

/**
 * Who has a connector, as a report: every agent (the primary agent included) and every person with it, their level on the
 * workspace key and, where people use their own login, whether theirs is connected. Nothing changes here: a grant and a
 * level are changed on the agent's page or the person's.
 */
export function ConnectorAccessReport({ connector, access }: { connector: ConnectorDTO; access: ConnectorAccess | null }) {
  const [who, setWho] = useTabParam(ACCESS_FILTERS, "who");
  const own = connector.credentials === "own";
  const agents: Row[] = (access?.agents ?? []).map((a) => ({
    key: a.agentId, kind: "agent", name: a.name, to: `/admin/agents/${a.agentId}?tab=connectors`,
    detail: a.primary ? "Primary agent" : null,
    level: levelLabel(connector, a.level), login: null, lastUsedAt: null,
  }));
  const people: Row[] = (access?.people ?? []).map((p) => ({
    key: p.userId, kind: p.kind === "service" ? "service" : "person", name: p.name, to: `/admin/users?q=${encodeURIComponent(p.email)}`,
    detail: p.email, level: levelLabel(connector, p.level), login: p.login, lastUsedAt: p.lastUsedAt,
  }));
  const rows = who === "agents" ? agents : who === "people" ? people : [...agents, ...people];
  const empty = !access
    ? "Loading…"
    : who === "agents"
      ? "No agent has it yet: grant it on the agent's page."
      : who === "people"
        ? "Nobody has access yet: grant it in Users."
        : `Nobody has ${connector.name} yet: grant it on an agent's page or in Users.`;
  return (
    <Table
      rows={rows}
      rowKey={(r) => r.key}
      empty={empty}
      filters={
        <div className="space-y-2">
          {connector.platformUses?.length ? <p className="text-sm text-muted">{usedByLine(connector.platformUses, 0)}.</p> : null}
          <Tabs
            size="sm"
            label="Who"
            value={who}
            onChange={setWho}
            tabs={[
              { key: "all", label: "All", count: access ? agents.length + people.length : null },
              { key: "agents", label: "Agents", count: access ? agents.length : null },
              { key: "people", label: "People", count: access ? people.length : null },
            ]}
          />
          <p className="text-xs text-muted">Change it on the agent or the person.</p>
        </div>
      }
      columns={[
        {
          header: "Name",
          primary: true,
          cell: (r) => (
            <div className="min-w-0">
              <TextLink to={r.to} quiet className="font-medium">{r.name}</TextLink>
              {r.detail && <div className="truncate text-xs text-muted">{r.detail}</div>}
            </div>
          ),
        },
        { header: "Type", cell: (r) => <Badge tone={r.kind === "agent" ? "violet" : "zinc"}>{r.kind === "agent" ? "Agent" : r.kind === "service" ? "Service account" : "Person"}</Badge> },
        {
          // An internal tool has no key: the column is the level each one reaches it at.
          header: connector.credentials === "internal" ? "Level" : "Workspace key",
          className: "text-xs",
          // Read without its heading on a phone, so it names itself there.
          cell: (r) => (
            <>
              <span className="text-muted lg:hidden">{connector.credentials === "internal" ? "Level" : "Workspace key"}: </span>
              {r.kind !== "agent" && own ? <span className="text-faint" title="People's runs use their own login here, never the workspace key">not used</span> : r.level}
            </>
          ),
        },
        ...(own
          ? [{
              header: "Own login",
              cell: (r: Row) =>
                r.kind === "agent" ? <span className="text-xs text-faint" title="Agents working on their own use the workspace key">—</span>
                  : r.login === "connected" ? <Badge tone="emerald">Connected</Badge>
                  : r.login === "lapsed" ? <Badge tone="amber">Lapsed</Badge>
                  : <span className="text-xs text-muted">Not connected</span>,
            }]
          : []),
        { header: "Last used", className: "text-xs text-muted whitespace-nowrap", cell: (r: Row) => (r.lastUsedAt ? `Used ${timeAgo(r.lastUsedAt)}` : r.login ? "Never used" : "") },
      ]}
    />
  );
}
